import React, { useState, useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import PopupModal from "../components/PopupModal/PopupModal";
import { TokenService } from "../services/Api";

// ==================== PROTECTED ROUTE ====================
/**
 * Protects routes that require authentication.
 * - If not logged in → redirect to /login
 * - If logged in but wrong role → show "Not Authorized" popup, navigate(-1), reload
 */
export const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [modalState, setModalState] = useState({
    open: false,
    type: "error",
    message: "",
  });

  const roleId = Number(user?.role?.id);
  const isAuthorized =
    allowedRoles.length === 0 || allowedRoles.includes(roleId);

  useEffect(() => {
    if (!loading && user && !isAuthorized) {
      setModalState({
        open: true,
        type: "error",
        message: "You are not authorized to access this screen.",
      });
    }
  }, [loading, user, isAuthorized]);

  const handleNotAuthorizedOk = () => {
    setModalState({ open: false, type: "error", message: "" });
    navigate(-1);
    setTimeout(() => window.location.reload(), 100);
  };

  if (loading) return null;

  // Not logged in → redirect to login (preserve where they came from)
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Logged in but wrong role → show popup
  if (!isAuthorized) {
    return (
      <PopupModal
        open={modalState.open}
        title="Access Denied"
        message={modalState.message}
        onOk={handleNotAuthorizedOk}
      />
    );
  }

  return children;
};

// ==================== PUBLIC ROUTE ====================
/**
 * For pages like Login / Forgot Password.
 * If user is already logged in, redirect them to their dashboard.
 *
 * Pass `allowWhenLoggedIn` to skip the dashboard redirect — use this for
 * public pages (like admission forms) that should remain accessible
 * even when a user is already logged in.
 */
export const PublicRoute = ({ children, allowWhenLoggedIn = false }) => {
  const { user, loading, loginInProgress } = useAuth();

  if (loading) return null;
  if (loginInProgress) return children;

  const hasToken = !!TokenService.getToken();
  const isReallyLoggedIn = user && hasToken;

  if (isReallyLoggedIn && !allowWhenLoggedIn) {
    const roleId = Number(user?.role?.id);
    if (roleId === 1) return <Navigate to="/dashboard" replace />;
    if ([2, 3, 4, 5, 6].includes(roleId))
      return <Navigate to="/user/dashboard" replace />;
  }

  return children;
};
